// src/utils/calendar.ts — kalenterilinkit
//
// Apple-kalenterin nappi ei tehnyt natiiviapissa yhtaan mitaan. Syy oli
// toteutustapa: .ics rakennettiin selaimessa Blobiksi ja ladattiin
// `<a download>` -klikkauksella. WKWebView ei tue blob-latauksia eika
// download-attribuuttia, joten klikkaus katosi tyhjaan ilman virhetta.
// Google-nappi vieressa toimi, koska se on tavallinen https-linkki.
//
// Nyt .ics tulee samalla tavalla osoitteesta: `eventIcs`-funktio
// (functions/src/index.ts) rakentaa tiedoston kyselyparametreista ja
// palauttaa sen text/calendar-tyyppisena. Sama linkki toimii verkossa,
// iOS:lla ja Androidilla.

import { ClubEvent } from "../types";

/** Tapahtuman otsikko kalenterimerkintaan. */
function calendarTitle(ev: ClubEvent): string {
  const sport = ev.sport.charAt(0).toUpperCase() + ev.sport.slice(1);
  return ev.title || `${sport} Americano – ${ev.venue || "Spinloop"}`;
}

/**
 * Kalenterimerkinnan kuvaus.
 *
 * Kumpikin kalenterilinkki vie sisallon ulos laitteelta: .ics kulkee
 * spinloop.app-osoitteen kautta ja Google-linkki Googlen palvelimille.
 * Kummassakin teksti paatyy myos selaimen historiaan. Siksi kuvaus
 * kootaan tassa tapahtuman omista kentista eika oteta vastaan valmiina.
 *
 * Aiemmin yllapitajan nakyma antoi molemmille `inviteText`-muuttujan,
 * jossa on rivi "Tapahtuman PIN: 1234". PIN on kirjautumisen salasana,
 * eika se kuulu osoiteriville.
 */
function calendarDescription(ev: ClubEvent): string {
  const rows = [`Spinloop ${ev.sport} ${ev.gameType || "Americano"}`];
  if (ev.venue) rows.push(`📍 ${calendarLocation(ev)}`);
  if (ev.panos) rows.push(`💰 ${ev.panos} €`);
  rows.push("", "spinloop.app");
  return rows.join("\n");
}

/**
 * Kalenterin sijaintirivi: paikan nimi ja osoite pilkulla erotettuna.
 * Osoite on se osa, josta puhelin osaa tarjota navigointia; pelkka
 * hallin nimi ei siihen riita.
 */
function calendarLocation(ev: ClubEvent): string {
  return [ev.venue, ev.venueAddress].filter(Boolean).join(", ");
}

/**
 * Osoite, josta selain tai kayttojarjestelma hakee .ics-tiedoston.
 *
 * Kuvaus kootaan tapahtuman kentista; PIN-koodia ei oteta vastaan
 * eika sita voi valittaa tanne vahingossa.
 */
export function icsUrl(ev: ClubEvent): string {
  const q = new URLSearchParams({
    start: new Date(ev.datetime).toISOString(),
    hours: "2",
    title: calendarTitle(ev),
    venue: calendarLocation(ev),
    desc: calendarDescription(ev),
    uid: ev.id || String(Date.now()),
  });
  return `https://spinloop.app/ics?${q.toString()}`;
}

/** Google-kalenterin valmis lomakeosoite. */
export function googleCalUrl(ev: ClubEvent): string {
  const description = calendarDescription(ev);
  const stamp = (iso: string) =>
    new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const endDt = new Date(ev.datetime);
  endDt.setHours(endDt.getHours() + 2);
  const q = new URLSearchParams({
    action: "TEMPLATE",
    text: calendarTitle(ev),
    dates: `${stamp(ev.datetime)}/${stamp(endDt.toISOString())}`,
    details: description,
    location: calendarLocation(ev),
  });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}
